import { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import ToggleSwitch from '../../common/ToggleSwitch'
import Dropdown from '../../common/Dropdown'
import TooltipedIcon from '../../common/TooltipedIcon'
import LoadingSpinner from '../../common/LoadingSpinner'
import { InformationIcon, ExpandLessIcon, ExpandMoreIcon } from '../../../icons'
import { getProductModelsVariants } from '../../../utils/requests/productModelsAPI'
import { capitalizeFirstLetter } from '../../../utils/functions'
import '../uploads.scss'

const ViewProductModels = (props) => {
  const { t } = useTranslation()
  const { modelDetails, handleEditModel, closeModal } = props
  const boolStatuses = ['no', 'yes']
  const [areVariantsExpanded, setAreVariantsExpanded] = useState(true)
  const [relatedVariants, setRelatedVariants] = useState([])
  const [isLoadingVariants, setIsLoadingVariants] = useState(false)
  const [hasLoadedVariants, setHasLoadedVariants] = useState(false)

  useEffect(() => {
    if (areVariantsExpanded && !hasLoadedVariants && modelDetails?.name) {
      setIsLoadingVariants(true)
      getProductModelsVariants({ model: modelDetails.name })
        .then((res) => {
          setRelatedVariants(res?.data?.items || res?.data || [])
          setHasLoadedVariants(true)
        })
        .catch(() => {
          setRelatedVariants([])
        })
        .finally(() => {
          setIsLoadingVariants(false)
        })
    }
  }, [areVariantsExpanded, hasLoadedVariants, modelDetails?.name])

  const boolFromAnything = (v) => {
    if (typeof v === 'boolean') return v
    if (typeof v === 'string') return ['true', '1', 'y', 'yes'].includes(v.toLowerCase())
    if (typeof v === 'number') return v === 1
    return false
  }

  const headerContent = () => {
    const isActive = boolFromAnything(modelDetails?.isActive)
    const statusKey = isActive ? 'active' : 'inactive'
    return (
      <div className="status-wrapper">
        <div className="label d-flex flex-justify-start flex-align-center">
          <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('product_models_context.model_status_info')}`} orientation="bottom" />
          {t('status')}
        </div>
        <Dropdown
          options={[
            { key: 'active', value: t('active') },
            { key: 'inactive', value: t('inactive') },
          ]}
          className="dropdown-input"
          isDisabled={true}
          initialSelectedKey={statusKey}
        />
      </div>
    )
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className="uploads-modal models-wrapper"
        title={t('details')}
        headerContent={headerContent()}
        footerContent={
          <>
            <SimpleButton className="cancel-button" onClick={closeModal}>
              {t('close')}
            </SimpleButton>
            <SimpleButton className="action-button" onClick={handleEditModel}>
              {t('edit')}
            </SimpleButton>
          </>
        }
      >
        <div className="uploads-modal-content">
          <div className="section-title">{t('product_models_context.general_model_details').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('model')}</p>
              <div className="value">{modelDetails?.name?.toUpperCase() || '-'}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('product_type')}</p>
              <div className="value">{modelDetails?.productType?.name || '-'}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('category')}</p>
              <div className="value">{modelDetails?.category?.name || '-'}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('dealer-portal.brand')}</p>
              <div className="value">{modelDetails?.brand?.name || '-'}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.device_function')}</p>
              <div className="value">{modelDetails?.deviceFunction?.name || '-'}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('product_models_context.technology')}</p>
              <div className="value">{modelDetails?.technology?.name || '-'}</div>
            </div>
          </div>

          <div className="separator" />
          <div className="section-title">{t('product_models_context.website_access').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option flex-justify-start">
                {t('product_models_context.dashboard_access')}
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('product_models_context.dashboard_access_info')}`} />
              </p>
              <div className="d-flex gap-8">
                <ToggleSwitch checked={boolFromAnything(modelDetails?.dashboardAccess)} isDisabled={true} />
                <span className="status">{t(boolStatuses[Number(boolFromAnything(modelDetails?.dashboardAccess))])}</span>
              </div>
            </div>
            <div className="input-wrapper">
              <p className="input-option flex-justify-start">
                {t('product_models_context.core_units')}
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('product_models_context.core_units_info')}`} orientation="right" />
              </p>
              <div className="d-flex gap-8">
                <ToggleSwitch checked={boolFromAnything(modelDetails?.coreUnit)} isDisabled={true} />
                <span className="status">{t(boolStatuses[Number(boolFromAnything(modelDetails?.coreUnit))])}</span>
              </div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option flex-justify-start">
                {t('product_models_context.token_activation')}
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('product_models_context.token_activation_info')}`} />
              </p>
              <div className="d-flex gap-8">
                <ToggleSwitch checked={boolFromAnything(modelDetails?.tokenActivation)} isDisabled={true} />
                <span className="status">{t(boolStatuses[Number(boolFromAnything(modelDetails?.tokenActivation))])}</span>
              </div>
            </div>
            <div className="input-wrapper"></div>
          </div>

          <div className="separator" />
          <div className="units-header" onClick={() => setAreVariantsExpanded((prev) => !prev)}>
            <div className="header-title">
              <div className="title">{t('product_models_context.related_variants')}</div>
              <div className="subtitle">{hasLoadedVariants && <span className="highlight">{relatedVariants.length}</span>}</div>
            </div>
            <div className="expand-button">{areVariantsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</div>
          </div>
          {areVariantsExpanded && (
            <>
              {isLoadingVariants && (
                <div className="expandable-loading-wrapper">
                  <LoadingSpinner />
                </div>
              )}
              {!isLoadingVariants && relatedVariants.length === 0 && hasLoadedVariants && (
                <div className="expandable-empty-state">
                  <p>{t('product_models_context.no_variants_found')}</p>
                </div>
              )}
              {!isLoadingVariants &&
                relatedVariants.map((variant, index) => (
                  <div
                    className={'item-wrapper' + (index === 0 ? ' first-item-wrapper' : '') + (index === relatedVariants.length - 1 ? ' last-item-wrapper' : '')}
                    key={variant.id || index}
                  >
                    <div className="item-title-wrapper">
                      <p className="title">{variant.modelNumber || '-'}</p>
                      <p className="subtitle">{variant.productModelName || '-'}</p>
                    </div>
                    <div className="item-status-wrapper">
                      <p className={variant.status === 'active' ? 'is-registered' : 'not-registered'}>{capitalizeFirstLetter(variant.status || '')}</p>
                    </div>
                  </div>
                ))}
            </>
          )}
        </div>
      </PageModal>
    </>
  )
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(dispatch),
  }
}

export default connect(dispatchToProps)(ViewProductModels)

